/**
 * AppShell — DS-01.
 *
 * The frame every authenticated screen renders inside. Three fixed bands stacked
 * in a column that is exactly the viewport tall; the content area is the only
 * region in the application that scrolls vertically. That is the whole point of
 * DS-01: a desktop application, not a web page.
 */

import { forwardRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { useClinicFavicon } from '@/features/settings/hooks';
import { TitleBar } from './TitleBar';
import { MenuBar } from './MenuBar';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';

export interface AppShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Defaults to the router outlet; pass children to mount the shell outside a route. */
  children?: ReactNode;
}

export const AppShell = forwardRef<HTMLDivElement, AppShellProps>(function AppShell(
  { className, children, ...rest },
  ref,
) {
  // NFR-05 — the rail's stand-in below 1024px. The state lives here because the
  // button that opens it is in the title bar and the sheet that answers is a
  // sibling of both; no other screen has any business knowing about it.
  const [navOpen, setNavOpen] = useState(false);
  // DS-05 — the desktop rail's collapsed state is a persisted preference owned
  // by the menu bar's toggle, so the shell only reads it to mount or unmount.
  const sidebarCollapsed = useUiStore((state) => state.sidebarCollapsed);
  // The clinic logo from /settings becomes the tab icon once someone is signed in.
  useClinicFavicon(Boolean(useAuthStore((state) => state.user)));

  return (
    // DS-01 — the page itself never scrolls.
    //
    // `100dvh` below 1024px: `h-screen` is `100vh`, which mobile Safari measures
    // with its address bar *hidden*. The shell is then taller than the viewport
    // actually is, and the bottom of every screen — a dialog footer, a save bar,
    // the last row of a table — sits under the browser chrome until the user
    // scrolls the page that DS-01 says never scrolls.
    <div
      ref={ref}
      className={cn('flex h-screen flex-col overflow-hidden max-lg:h-[100dvh]', className)}
      {...rest}
    >
      <TitleBar onOpenNav={() => setNavOpen(true)} />
      <MenuBar />

      <div className="flex flex-1 overflow-hidden">
        {/* DS-50 — the sidebar is first in the DOM, so `dir=rtl` lands it on the
            right and English lands it on the left, with no physical class and no
            branch on the language anywhere in the shell. */}
        {sidebarCollapsed ? null : <Sidebar />}

        {/* DS-07 — the one vertically scrolling region. `min-w-0` keeps a wide
            table scrolling inside itself instead of pushing the rail off screen. */}
        <main
          id="app-content"
          className="touch-scroll min-w-0 flex-1 overflow-auto bg-slate-100 p-4
                     max-lg:p-2 max-lg:pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]"
        >
          {children ?? <Outlet />}
        </main>
      </div>

      {/* Mounted at every width but only reachable below 1024px, where the title
          bar renders the hamburger that opens it. A closed Radix dialog renders
          nothing, so the desktop pays for a boolean and no DOM. */}
      <MobileNav open={navOpen} onOpenChange={setNavOpen} />
    </div>
  );
});

export default AppShell;
